"use client";

import { useState, useMemo } from "react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import type { Locale } from "date-fns";
import { isDateBlocked, isDateRangeBlocked } from "@/lib/utils/rental-blocks";
import {
  calculateRentalDays,
  calculateSeasonalDailyV2Price,
  calculateSeasonalRentalPrice,
  getSeasonalTimeslotPrice,
} from "@/lib/utils/pricing";
import {
  getDailyV2RateForDays,
  getDailyV2TiersForSub,
  type MergedDailySubProduct,
} from "@/lib/utils/daily-matrix";
import {
  buildCheckoutData,
  saveAndNavigateToCheckout,
  type CheckoutData,
} from "@/lib/utils/checkout";
import { getCartItemCount } from "@/app/actions/cart";
import { customerPolicyTotalFromTable } from "@/lib/pricing/base-plus-extra";
import { dispatchCartUpdate } from "@/components/cart/CartButton";
import { useAddToCartGuard } from "@/hooks/useAddToCartGuard";
import { useToast } from "@/hooks/use-toast";
import { toast as sonnerToast } from "sonner";
import type { CartItem } from "@/types/booking";
import type {
  ProductDetail,
  ProductRentalBlock,
  BranchOperationBlock,
  AddToCartInput,
  CartItemOptionSelection,
} from "@/types";
import type { SupportedCurrency } from "@/lib/utils/currency";
import { canReturnAtBranch } from "@/lib/mobility/one-way-return";
import {
  isPerDayOptionOrder,
  perDayRentalDays,
  readPerDayOptionOrders,
} from "@/lib/mobility/per-day-options";
import type {
  MergedSubProduct,
  MergedTimeOption,
} from "@/lib/utils/timeslot-matrix";

interface UseBookingCartParams {
  product: ProductDetail;
  pricingMode: "daily" | "timeslot";
  timeSlots: Array<{
    id: string;
    durationHours?: number;
    price: number;
    label?: string;
    displayOrder?: number;
  }>;
  /**
   * Timeslot v2 매트릭스 (Phase B-3) — 언어 병합 완료본. 존재하면 timeslot 모드가
   * [이용 시간 옵션 × 세부 상품] 선택으로 동작 (v1 timeSlots는 빈 배열).
   */
  timeslotMatrix?: {
    timeOptions: MergedTimeOption[];
    subProducts: MergedSubProduct[];
  } | null;
  /**
   * 일일대여 v2 세부 상품 (08-17) — **판매 가능한 것만** 걸러 넘어온다(BookingCard).
   * 존재하면 daily 모드도 세부 상품 좌표가 있어야 cart에 담긴다(fail-closed).
   */
  dailyMatrix?: {
    subProducts: MergedDailySubProduct[];
  } | null;
  /** v2 세부 상품 잔여 재고 (sub_display_order → remaining). 없는 좌표 = 무제한 */
  subRemaining?: Map<number, number>;
  cartItems: CartItem[];
  setCartItems: React.Dispatch<React.SetStateAction<CartItem[]>>;
  locale: string;
  dateFnsLocale: Locale;
  sourceCurrency: SupportedCurrency;
  isAuthenticated: boolean;
  onAuthRequired?: () => void;
  onCheckout?: (data: CheckoutData) => void;
  variant: "desktop" | "mobile";
  hasPickupOptions: boolean;
  isDelivery: (id: string) => boolean;
  isShipping: (id: string) => boolean;
  productBlocks: ProductRentalBlock[];
  branchBlocks: BranchOperationBlock[];
  initialPickupDate?: Date;
  initialReturnDate?: Date;
  /**
   * 초기 픽업 지점 id — 모빌리티 허브의 `?branch=` 딥링크(설계 §3-4).
   * 호출측(ProductDetailClient)이 `product.branches` 안에 있는 id 만 넘긴다.
   */
  initialPickupBranch?: string;
  /**
   * 반납 지점을 수령 지점으로 기본 선택 — 편도 반납 요금 옵션이 지정된 상품만
   * (`getOneWayReturnOption`, 09-22). 수령 지점이 반납 가능 지점일 때만 따라가고,
   * 고객이 반납을 따로 바꾼 뒤에는 수령을 다시 바꿔도 덮어쓰지 않는다.
   */
  returnDefaultsToPickup?: boolean;
  /**
   * 수령한 곳에서 반납(`specifications.returnAtPickup`, 10-01) — 반납 지점 선택이
   * 없고 반납 = 수령 지점으로 항상 고정한다. 반납 가능 지점 판정(`canReturnAtBranch`)
   * 은 보지 않는다: 플래그 자체가 "수령 장소가 곧 반납 장소"라는 파트너 선언이고,
   * 서버(addToCart·getCart·결제 검증)도 같은 규칙으로 덮어쓴다.
   */
  returnAtPickup?: boolean;
  /** rental이 cart에 담기고 폼이 리셋된 직후 호출 (옵션 섹션 자동 스크롤 트리거 등) */
  onRentalAdded?: () => void;
}

/**
 * 하루당 옵션(09-30)에 곱할 한 대여 라인의 일수 — 서버(validateDirectCheckoutPrice·
 * getCart)와 같은 규칙: daily = 기본 요금과 같은 `calculateRentalDays`, 타임슬롯 = 1.
 */
function rentalDaysForPerDay(
  item: CartItem,
  pricingMode: "daily" | "timeslot",
): number {
  const isTimeslot = item.optionDisplayOrder != null || pricingMode === "timeslot";
  return perDayRentalDays(
    isTimeslot ? "timeslot" : "daily",
    calculateRentalDays(item.pickupDate, item.returnDate),
  );
}

function formatDateToLocalISO(date: Date | undefined): string | undefined {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function useBookingCart({
  product,
  pricingMode,
  timeSlots,
  timeslotMatrix,
  dailyMatrix,
  subRemaining,
  cartItems,
  setCartItems,
  locale,
  dateFnsLocale,
  sourceCurrency,
  isAuthenticated,
  onAuthRequired,
  onCheckout,
  variant,
  hasPickupOptions,
  isDelivery,
  isShipping,
  productBlocks,
  branchBlocks,
  initialPickupDate,
  initialReturnDate,
  initialPickupBranch,
  returnDefaultsToPickup = false,
  returnAtPickup = false,
  onRentalAdded,
}: UseBookingCartParams) {
  const router = useRouter();
  const t = useTranslations("product.booking");
  const tMethods = useTranslations("product.usageMethods");
  const { toast } = useToast();
  const { addToCartWithGuard, guardDialog: addToCartGuardDialog } =
    useAddToCartGuard();

  const getMethodName = (methodType: "pickup" | "delivery" | "shipping") => {
    return tMethods(methodType);
  };

  // Form state
  const [selectedPickupBranch, setSelectedPickupBranch] = useState<string>(
    initialPickupBranch ?? "",
  );
  const [selectedReturnBranch, setSelectedReturnBranch] = useState<string>(() => {
    // 수령한 곳에서 반납: 딥링크 수령 지점이 있으면 반납도 그 지점
    if (returnAtPickup) return initialPickupBranch ?? "";
    // 딥링크 수령 지점도 반납 기본값의 대상 — 허브에서 고른 지점이 곧 반납 지점
    return returnDefaultsToPickup &&
      initialPickupBranch &&
      canReturnAtBranch(product.branches, initialPickupBranch)
      ? initialPickupBranch
      : "";
  });
  const [pickupDate, setPickupDate] = useState<Date | undefined>(initialPickupDate);
  const [returnDate, setReturnDate] = useState<Date | undefined>(initialReturnDate);
  const [pickupTime, setPickupTime] = useState<string>("");
  const [returnTime, setReturnTime] = useState<string>("");
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>("");
  // ── Timeslot v2 선택 상태 (Phase B-3): 이용 시간 옵션 = 첫 칩 기본 선택,
  //    세부 상품 = 선택 즉시 cart 담김(행 클릭 → checkAndAddRentalToCart)
  const isTimeslotV2 =
    pricingMode === "timeslot" &&
    !!timeslotMatrix &&
    timeslotMatrix.timeOptions.length > 0 &&
    timeslotMatrix.subProducts.length > 0;
  const [selectedTimeOptionOrder, setSelectedTimeOptionOrder] =
    useState<number>(timeslotMatrix?.timeOptions[0]?.displayOrder ?? 0);
  const [selectedSubOrder, setSelectedSubOrder] = useState<number | null>(null);
  const [isPickupOpen, setIsPickupOpen] = useState(false);
  const [isDateDrawerOpen, setIsDateDrawerOpen] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<
    Record<string, { enabled: boolean; value?: string | number }>
  >({});

  // UI state
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showCartConfirmation, setShowCartConfirmation] = useState(false);
  const [optionErrorsVisible, setOptionErrorsVisible] = useState(false);

  // ── 하루당 옵션(09-30): 플래그 display_order 집합 + 담긴 대여 라인들의 일수 합.
  //    옵션은 담긴 대여마다 한 벌씩 붙어 장바구니에 들어가므로(handleAddToCart) 총액도
  //    "단가 × Σ일수". 대여가 아직 없으면 null — 화면은 "날짜를 고르면 계산" 안내.
  const perDayOrders = useMemo(
    () => readPerDayOptionOrders(product.specifications),
    [product.specifications],
  );
  const perDayDays = useMemo(() => {
    const rentals = cartItems.filter((item) => item.type === "rental");
    if (rentals.length === 0) return null;
    return rentals.reduce(
      (sum, item) => sum + rentalDaysForPerDay(item, pricingMode),
      0,
    );
  }, [cartItems, pricingMode]);
  const isPerDayOptionId = (optionId: string): boolean => {
    const option = product.options?.find((opt) => opt.id === optionId);
    return (
      option?.option_type === "select" &&
      isPerDayOptionOrder(perDayOrders, option.display_order)
    );
  };

  // Total price (성수기 재정의 반영 — 서버 검증과 동일 함수 사용)
  const totalPrice = useMemo(() => {
    return cartItems.reduce((total, item) => {
      if (item.type === "rental") {
        // Timeslot v2: 셀 단가 × 수량. 성수기는 (sub, option) 행으로 재정의(09-21).
        if (item.optionDisplayOrder != null && item.unitPrice != null) {
          const unit = getSeasonalTimeslotPrice(
            item.unitPrice,
            item.optionDisplayOrder,
            item.pickupDate,
            product.pricing_seasons,
            item.subDisplayOrder ?? null
          ).total;
          return total + unit * item.quantity;
        }
        // Daily v2 (08-17): 세부 상품 티어 단가 × 대여일수 × 수량. 성수기는
        // (sub, tier) 행으로 일별 재정의(09-21) — 서버 검증·Edge와 같은 함수.
        // 타임슬롯 v2와 달리 optionDisplayOrder가 없다(축이 세부 상품 하나뿐).
        if (
          item.subDisplayOrder != null &&
          item.unitPrice != null &&
          item.pickupDate &&
          item.returnDate
        ) {
          const days = calculateRentalDays(item.pickupDate, item.returnDate);
          const policyTotal = customerPolicyTotalFromTable({
            table: product.policyTable,
            subDisplayOrder: item.subDisplayOrder,
            days,
            pickupDate: item.pickupDate,
            markedSeasons: product.pricing_seasons,
          });
          return (
            total +
            (policyTotal == null ? calculateSeasonalDailyV2Price(
              item.pickupDate,
              item.returnDate,
              item.quantity,
              getDailyV2TiersForSub(product.day_prices, item.subDisplayOrder),
              item.subDisplayOrder,
              product.pricing_seasons
            ).total : policyTotal * item.quantity)
          );
        }
        if (item.pickupDate && item.returnDate) {
          return (
            total +
            calculateSeasonalRentalPrice(
              item.pickupDate,
              item.returnDate,
              item.quantity,
              product.pricing || [],
              product.pricing_seasons
            ).total
          );
        } else if (item.timeSlotPrice) {
          const unit = getSeasonalTimeslotPrice(
            item.timeSlotPrice,
            item.timeSlotDisplayOrder,
            item.pickupDate,
            product.pricing_seasons,
            null
          ).total;
          return total + unit * item.quantity;
        }
      } else if (item.type === "option") {
        // 하루당 옵션: 수량 대신 담긴 대여의 일수(없으면 담긴 수량 = 옵션 단독 구매 규칙)
        const qty = item.perDay ? (perDayDays ?? item.quantity) : item.quantity;
        return total + (item.optionPrice || 0) * qty;
      }
      return total;
    }, 0);
  }, [cartItems, perDayDays, product.pricing, product.day_prices, product.pricing_seasons, product.policyTable]);

  // Date blocked helpers (uses pure functions directly to avoid circular deps).
  // branchId 는 실제 지점 uuid 이거나 "delivery"/"shipping" 유사 id — 후자는
  // 택배 제한(method_type 행) 매칭에 그대로 쓰인다.
  const isDateBlockedForBranch = (date: Date, branchId: string) => {
    if (!branchId) return false;
    return isDateBlocked(date, product.id, branchId, productBlocks, branchBlocks);
  };
  const isRangeBlockedForBranch = (start: Date, end: Date, branchId: string) => {
    if (!branchId) return false;
    return isDateRangeBlocked(start, end, product.id, branchId, productBlocks, branchBlocks);
  };

  // Unified pickup branch change handler
  const handlePickupBranchChange = (value: string) => {
    setSelectedPickupBranch(value);
    setPickupTime("");

    // 반납 기본값 = 수령 지점(opt-in). 반납이 아직 비었거나 직전 수령 지점을
    // 그대로 따르고 있을 때만 갱신 — 고객이 고른 별도 반납 지점은 건드리지 않는다.
    let nextReturnBranch: string | undefined;
    if (returnAtPickup) {
      // 수령한 곳에서 반납: 반납은 항상 수령 지점과 같이 움직인다.
      nextReturnBranch = value;
      if (nextReturnBranch !== selectedReturnBranch) {
        setSelectedReturnBranch(nextReturnBranch);
        setReturnTime("");
      }
    } else if (
      returnDefaultsToPickup &&
      (selectedReturnBranch === "" || selectedReturnBranch === selectedPickupBranch)
    ) {
      nextReturnBranch = canReturnAtBranch(product.branches, value) ? value : "";
      if (nextReturnBranch !== selectedReturnBranch) {
        setSelectedReturnBranch(nextReturnBranch);
        setReturnTime("");
      }
    }

    // Reset dates if blocked by the new branch/method. 택배·숙소 배송을 고른
    // 경우에도 그 방법에만 걸린 제한(method_type)이 있을 수 있으므로 검사한다.
    if (value && pickupDate) {
      const isTimeslot = pricingMode === "timeslot";
      const blocked =
        isTimeslot || !returnDate
          ? isDateBlockedForBranch(pickupDate, value)
          : isRangeBlockedForBranch(pickupDate, returnDate, value);

      if (blocked) {
        setPickupDate(undefined);
        setReturnDate(undefined);
        setReturnTime("");
        sonnerToast.error(t("dateResetOnBranchChange.title"), {
          description: t("dateResetOnBranchChange.description"),
        });
      }
    }

    checkAndAddRentalToCart({
      newPickupBranch: value,
      ...(nextReturnBranch !== undefined ? { newReturnBranch: nextReturnBranch } : {}),
    });
  };

  // Unified return branch change handler
  const handleReturnBranchChange = (value: string) => {
    setSelectedReturnBranch(value);
    setReturnTime("");

    if (value && returnDate) {
      const isTimeslot = pricingMode === "timeslot";
      const blocked =
        isTimeslot || !pickupDate
          ? isDateBlockedForBranch(returnDate, value)
          : isRangeBlockedForBranch(pickupDate, returnDate, value);

      if (blocked) {
        setReturnDate(undefined);
        setReturnTime("");
        sonnerToast.error(t("dateResetOnBranchChange.title"), {
          description: t("dateResetOnBranchChange.description"),
        });
      }
    }

    checkAndAddRentalToCart({ newReturnBranch: value });
  };

  // Check and auto-add rental to cart when all fields complete
  const checkAndAddRentalToCart = (overrides: {
    newPickupBranch?: string;
    newReturnBranch?: string;
    newPickupDate?: Date;
    newReturnDate?: Date;
    newPickupTime?: string;
    newReturnTime?: string;
    newTimeSlot?: string;
    /** Timeslot v2: 이용 시간 옵션/세부 상품 display_order (행 클릭 시 전달) */
    newTimeOptionOrder?: number;
    newSubOrder?: number;
  } = {}) => {
    const pickup = overrides.newPickupBranch ?? selectedPickupBranch;
    // 수령한 곳에서 반납 상품은 state 와 무관하게 반납 = 수령 (담기는 값의 최종 가드)
    const returnB = returnAtPickup
      ? pickup
      : (overrides.newReturnBranch ?? selectedReturnBranch);
    const pDate = overrides.newPickupDate ?? pickupDate;
    const rDate = overrides.newReturnDate ?? returnDate;
    const pTime = overrides.newPickupTime ?? pickupTime;
    const rTime = overrides.newReturnTime ?? returnTime;
    const tSlot = overrides.newTimeSlot ?? selectedTimeSlot;

    const isPickupDelivery = isDelivery(pickup);
    const isReturnDelivery = isDelivery(returnB);
    const isPickupShipping = isShipping(pickup);
    const isReturnShipping = isShipping(returnB);

    if (pricingMode === "daily") {
      const pickupTimeRequired = !isPickupDelivery;
      const returnTimeRequired = !isReturnDelivery;
      const hasRequiredPickupTime = pickupTimeRequired ? !!pTime : true;
      const hasRequiredReturnTime = returnTimeRequired ? !!rTime : true;
      // ── Daily v2 (08-17): 세부 상품 좌표가 판매 단위. 좌표 없이는 담기지 않는다
      //    (fail-closed — 0원 판매·좌표 소실 차단). v1은 이 축 자체가 없으므로
      //    아래 조건이 항상 true라 동작 불변.
      // prop 존재 자체가 v2 신호 — subProducts가 비어 있어도(판매 가능한 세부 상품
      // 0개) v1 경로로 흘려보내지 않는다. 그 경우 아래 조건이 영구히 false라
      // 라인이 담기지 않는다(= 예약 불가, 0원 판매 차단).
      const isDailyV2 = !!dailyMatrix;
      const dailySubOrder = overrides.newSubOrder ?? selectedSubOrder;
      const dailySub = isDailyV2
        ? dailyMatrix!.subProducts.find(
            (sp) => sp.displayOrder === dailySubOrder,
          )
        : undefined;
      const hasRequiredDailySub = isDailyV2 ? !!dailySub : true;

      if (
        pickup &&
        returnB &&
        pDate &&
        rDate &&
        hasRequiredPickupTime &&
        hasRequiredReturnTime &&
        hasRequiredDailySub
      ) {
        const pickupBranch = product.branches?.find((b) => b.id === pickup);
        const returnBranch = product.branches?.find((b) => b.id === returnB);
        const rentalId = `rental-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

        // v2 단가 = 대여일수에 해당하는 구간의 일 단가(기본 티어가). 시즌 재정의는
        // 합산 경로(totalPrice·buildCheckoutData·CartItemsList)가 일별로 적용한다.
        // 0원이면 담지 않는다(fail-closed) — 서버 재가격도 같은 규칙으로 거부한다.
        let dailyV2UnitPrice: number | undefined;
        if (isDailyV2 && dailySub) {
          const rentalDays = calculateRentalDays(pDate, rDate);
          const rate = getDailyV2RateForDays(rentalDays, dailySub.tiers);
          if (!(rate > 0)) {
            // 조용한 무반응 금지 — 선택을 다 채웠는데 아무 일도 안 일어나면
            // 고객은 UI가 멈춘 것으로 읽는다. 실패 사유(해당 기간 단가 없음)를
            // 기존 daily v2 안내 문구로 알린다(신규 카피 없음).
            toast({
              title: t("error"),
              description: t("dailyV2Unavailable"),
              variant: "destructive",
            });
            return;
          }
          dailyV2UnitPrice = rate;
        }
        // 07-17 규칙 재사용: 세부상품명 = 상품명이면 생략(선택 내역 중복 방지)
        const dailySubNameDuplicates =
          !!dailySub?.name &&
          dailySub.name.trim() === (product.name ?? "").trim();

        setCartItems((prev) => {
          const newItem: CartItem = {
            id: rentalId,
            type: "rental",
            quantity: 1,
            pickupDate: pDate,
            pickupTime: isPickupDelivery ? undefined : pTime,
            returnDate: rDate,
            returnTime: isReturnDelivery ? undefined : rTime,
            pickupBranchId: pickup || undefined,
            pickupBranchName:
              pickupBranch?.name ||
              (isPickupDelivery ? getMethodName("delivery") : undefined) ||
              (isPickupShipping ? getMethodName("shipping") : undefined),
            pickupMethodType: isPickupDelivery
              ? "delivery"
              : isPickupShipping
                ? "shipping"
                : "pickup",
            returnBranchId: returnB || undefined,
            returnBranchName:
              returnBranch?.name ||
              (isReturnDelivery ? getMethodName("delivery") : undefined) ||
              (isReturnShipping ? getMethodName("shipping") : undefined),
            returnMethodType: isReturnDelivery
              ? "delivery"
              : isReturnShipping
                ? "shipping"
                : "pickup",
            // ── Daily v2 좌표 + 단가 + 표시 라벨 (v1은 전부 undefined — 동작 불변).
            //    optionDisplayOrder는 **싣지 않는다**: 이 좌표가 있으면
            //    resolveRentalType이 timeslot으로 오판정한다(Phase 0-A 규약).
            subDisplayOrder: dailySub?.displayOrder,
            unitPrice: dailyV2UnitPrice,
            subProductName: dailySubNameDuplicates
              ? undefined
              : dailySub?.name || undefined,
            // 주문당 최대 구매 수량 cap — null = 무제한 (08-10).
            // v2 = 세부 상품별 값(판매 단위 = 집계 단위), v1 = products 값.
            maxQuantity: isDailyV2
              ? (dailySub?.maxPurchaseQuantity ?? null)
              : (product.max_purchase_quantity ?? null),
            purchaseLimit: isDailyV2
              ? (dailySub?.maxPurchaseQuantity ?? null)
              : (product.max_purchase_quantity ?? null),
          };
          return [...prev, newItem];
        });

        // Reset fields
        setPickupDate(undefined);
        setReturnDate(undefined);
        setPickupTime("");
        setReturnTime("");
        setSelectedPickupBranch("");
        setSelectedReturnBranch("");
        setSelectedSubOrder(null);

        onRentalAdded?.();
      }
    } else if (isTimeslotV2 && timeslotMatrix) {
      // ── Timeslot v2 매트릭스 (Phase B-3): 컨텍스트(지점/날짜/시간) + 세부 상품
      //    선택 완료 시 담김. 이용 시간 옵션은 첫 칩 기본 선택이라 항상 존재.
      const optOrder = overrides.newTimeOptionOrder ?? selectedTimeOptionOrder;
      const subOrder = overrides.newSubOrder ?? selectedSubOrder;
      const isPickupDeliveryV2 = isDelivery(pickup);
      const isPickupShippingV2 = isShipping(pickup);
      const pickupTimeRequiredV2 =
        !isPickupDeliveryV2 && product.is_pickup_time_required !== false;
      const hasRequiredPickupTimeV2 = pickupTimeRequiredV2 ? !!pTime : true;
      const hasValidPickupV2 = hasPickupOptions ? !!pickup : true;

      if (hasValidPickupV2 && pDate && hasRequiredPickupTimeV2 && subOrder != null) {
        const subProduct = timeslotMatrix.subProducts.find(
          (sp) => sp.displayOrder === subOrder,
        );
        const optIdx = timeslotMatrix.timeOptions.findIndex(
          (opt) => opt.displayOrder === optOrder,
        );
        const timeOption = timeslotMatrix.timeOptions[optIdx];
        if (!subProduct || !timeOption) return;

        const unitPrice = subProduct.prices[optIdx] ?? 0;
        // 잔여 재고 — 조회 결과에 없는 좌표 = 무제한(null)
        const remaining = subRemaining?.get(subOrder);
        // 07-17: "단일 상품이면 생략" → "세부상품명 = 상품명이면 생략"(중복 방지)으로 교체
        // — Edge resolveTimeslotV2Label / web composeTimeslotV2Label과 동일 규칙.
        const subNameDuplicatesProduct =
          !!subProduct.name &&
          subProduct.name.trim() === (product.name ?? "").trim();
        const pickupBranch = product.branches?.find((b) => b.id === pickup);
        const rentalId = `rental-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const shouldOmitPickupTime =
          isPickupDeliveryV2 || product.is_pickup_time_required === false;

        setCartItems((prev) => {
          const newItem: CartItem = {
            id: rentalId,
            type: "rental",
            quantity: 1,
            pickupDate: pDate,
            pickupTime: shouldOmitPickupTime ? undefined : pTime,
            pickupBranchId: pickup || undefined,
            pickupBranchName:
              pickupBranch?.name ||
              (isPickupDeliveryV2 ? getMethodName("delivery") : undefined) ||
              (isPickupShippingV2 ? getMethodName("shipping") : undefined),
            pickupMethodType: isPickupDeliveryV2
              ? "delivery"
              : isPickupShippingV2
                ? "shipping"
                : "pickup",
            // v2 스냅샷 좌표 + 단가 + 표시 라벨 (B-4 checkout 페이로드로 전송 예정)
            subDisplayOrder: subOrder,
            optionDisplayOrder: timeOption.displayOrder,
            unitPrice,
            // 세부상품명 = 상품명이면 생략(중복 방지) → 선택 내역에 옵션 라벨만 (07-17 규칙)
            subProductName: subNameDuplicatesProduct
              ? undefined
              : subProduct.name || undefined,
            timeOptionLabel: timeOption.label || undefined,
            // v2 잔여 재고 cap + **세부 상품별** 주문당 최대 구매 수량 cap의 min 합류
            // (08-10 확장 — 판매 단위 = 집계 단위: v2는 subProduct 개별값이 진실)
            maxQuantity:
              remaining != null || subProduct.maxPurchaseQuantity != null
                ? Math.min(
                    remaining ?? Infinity,
                    subProduct.maxPurchaseQuantity ?? Infinity,
                  )
                : null,
            purchaseLimit: subProduct.maxPurchaseQuantity ?? null,
          };
          return [...prev, newItem];
        });

        setPickupDate(undefined);
        setPickupTime("");
        setSelectedPickupBranch("");
        setSelectedSubOrder(null);
        setSelectedTimeOptionOrder(
          timeslotMatrix.timeOptions[0]?.displayOrder ?? 0,
        );

        onRentalAdded?.();
      }
    } else {
      // Timeslot mode
      const isPickupDeliveryTS = isDelivery(pickup);
      const isPickupShippingTS = isShipping(pickup);
      const pickupTimeRequired =
        !isPickupDeliveryTS && product.is_pickup_time_required !== false;
      const hasRequiredPickupTime = pickupTimeRequired ? !!pTime : true;
      const hasValidPickup = hasPickupOptions ? !!pickup : true;

      if (hasValidPickup && pDate && hasRequiredPickupTime && tSlot) {
        const selectedSlot = timeSlots.find((slot) => slot.id === tSlot);
        const pickupBranch = product.branches?.find((b) => b.id === pickup);
        const rentalId = `rental-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        const shouldOmitPickupTime =
          isPickupDeliveryTS || product.is_pickup_time_required === false;

        setCartItems((prev) => {
          const newItem: CartItem = {
            id: rentalId,
            type: "rental",
            quantity: 1,
            pickupDate: pDate,
            pickupTime: shouldOmitPickupTime ? undefined : pTime,
            pickupBranchId: pickup || undefined,
            pickupBranchName:
              pickupBranch?.name ||
              (isPickupDeliveryTS ? getMethodName("delivery") : undefined) ||
              (isPickupShippingTS ? getMethodName("shipping") : undefined),
            pickupMethodType: isPickupDeliveryTS
              ? "delivery"
              : isPickupShippingTS
                ? "shipping"
                : "pickup",
            timeSlotId: tSlot,
            // duration은 optional — 없으면 label-only 슬롯
            timeSlotDuration: selectedSlot?.durationHours,
            timeSlotPrice: selectedSlot?.price || 0,
            timeSlotLabel: selectedSlot?.label,
            timeSlotDisplayOrder: selectedSlot?.displayOrder,
            // 주문당 최대 구매 수량 cap — null = 무제한 (08-10)
            maxQuantity: product.max_purchase_quantity ?? null,
            purchaseLimit: product.max_purchase_quantity ?? null,
          };
          return [...prev, newItem];
        });

        setPickupDate(undefined);
        setPickupTime("");
        setSelectedPickupBranch("");
        setSelectedTimeSlot("");

        onRentalAdded?.();
      }
    }
  };

  // Add select option to cart
  const addOptionToCart = (optionId: string, value: string | number) => {
    const option = product.options?.find((opt) => opt.id === optionId);
    if (option && option.choices) {
      const choice = option.choices.find((c) => c.label === value);
      if (choice && isPerDayOptionId(optionId)) {
        // 하루당 옵션(09-30): 한 옵션에 선택지 하나만(단일 선택). 다른 선택지를 누르면
        // 교체, 이미 고른 선택지를 다시 누르면 해제(미리보기 동작). 수량은 쓰지 않는다 —
        // 과금 수량 = 대여일수(perDayDays), 서버가 다시 강제한다.
        setCartItems((prev) => {
          const alreadySelected = prev.some(
            (item) =>
              item.type === "option" &&
              item.optionId === optionId &&
              item.optionValue === choice.label,
          );
          const rest = prev.filter(
            (item) => !(item.type === "option" && item.optionId === optionId),
          );
          if (alreadySelected) return rest;
          const newItem: CartItem = {
            id: `option-${optionId}-${choice.label}`,
            type: "option",
            quantity: 1,
            optionId,
            optionName: option.name,
            optionValue: choice.label,
            optionPrice: choice.price,
            choiceId: choice.id,
            optionType: "select",
            perDay: true,
            maxQuantity: 1,
          };
          return [...rest, newItem].sort((a: CartItem, b: CartItem) => {
            if (a.type === "rental" && b.type === "option") return -1;
            if (a.type === "option" && b.type === "rental") return 1;
            return 0;
          });
        });
        setSelectedOptions((prev) => {
          const newOptions = { ...prev };
          delete newOptions[optionId];
          return newOptions;
        });
      } else if (choice) {
        setCartItems((prev) => {
          const existingIndex = prev.findIndex(
            (item) =>
              item.type === "option" &&
              item.optionId === optionId &&
              item.optionValue === choice.label,
          );

          let newItems: CartItem[];
          if (existingIndex !== -1) {
            newItems = prev.map((item, index) =>
              index === existingIndex
                ? {
                    ...item,
                    quantity:
                      item.maxQuantity != null
                        ? Math.min(item.quantity + 1, item.maxQuantity)
                        : item.quantity + 1,
                  }
                : item,
            );
          } else {
            const newItem: CartItem = {
              id: `option-${optionId}-${choice.label}`,
              type: "option",
              quantity: 1,
              optionId,
              optionName: option.name,
              optionValue: choice.label,
              optionPrice: choice.price,
              choiceId: choice.id,
              optionType: "select",
              // 옵션의 주문당 최대 수량(product_options.max_quantity) — 선택 내역
              // 스테퍼 + 가 여기서 멈춘다(없으면 무제한, 기존 동작 불변).
              maxQuantity: option.max_quantity ?? null,
            };
            newItems = [...prev, newItem];
          }

          return newItems.sort((a: CartItem, b: CartItem) => {
            if (a.type === "rental" && b.type === "option") return -1;
            if (a.type === "option" && b.type === "rental") return 1;
            return 0;
          });
        });

        setSelectedOptions((prev) => {
          const newOptions = { ...prev };
          delete newOptions[optionId];
          return newOptions;
        });
      }
    }
  };

  const updateCartItemQuantity = (itemId: string, delta: number) => {
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === itemId
          ? {
              ...item,
              // Timeslot v2: 잔여 재고 cap (maxQuantity null/undefined = 무제한 — v1 동작 불변)
              quantity: Math.min(
                Math.max(1, item.quantity + delta),
                item.maxQuantity ?? Infinity,
              ),
            }
          : item,
      ),
    );
  };

  const removeCartItem = (itemId: string) => {
    setCartItems((prev) => prev.filter((item) => item.id !== itemId));
  };

  // 담긴 rental을 다시 폼 state로 복원하고 cart에서 제거 → selector 재펼침(편집).
  // cart item이 보유한 값을 대응 setter로 되돌린다. delivery/shipping sentinel id는
  // 그대로 set하면 BranchSelector가 인식한다.
  const editRentalItem = (itemId: string) => {
    const item = cartItems.find(
      (i) => i.id === itemId && i.type === "rental",
    );
    if (!item) return;

    setSelectedPickupBranch(item.pickupBranchId ?? "");
    setPickupDate(item.pickupDate);
    setPickupTime(item.pickupTime ?? "");

    if (pricingMode === "daily") {
      setSelectedReturnBranch(item.returnBranchId ?? "");
      setReturnDate(item.returnDate);
      setReturnTime(item.returnTime ?? "");
      // Daily v2: 세부 상품 좌표 복원 (타임슬롯 v2 복원 관례 미러 — 08-17).
      // 복원하지 않으면 [변경] 후 리스트가 미선택으로 돌아가 재담김이 막힌다.
      setSelectedSubOrder(item.subDisplayOrder ?? null);
    } else {
      setSelectedTimeSlot(item.timeSlotId ?? "");
      // Timeslot v2: 좌표 복원 (v1 슬롯 복원과 동일 관례 — 날짜 변경 시 재담김 동작 유지)
      if (item.optionDisplayOrder != null) {
        setSelectedTimeOptionOrder(item.optionDisplayOrder);
      }
      if (item.subDisplayOrder != null) {
        setSelectedSubOrder(item.subDisplayOrder);
      }
    }

    removeCartItem(itemId);
  };

  // BookingCard 단계에서는 select 타입 필수 옵션만 검사.
  // input/file/link 타입은 checkout 페이지에서 입력/업로드 → 거기서 자체 검증.
  // { id, name } 페어를 반환 — UI가 해당 옵션을 하이라이트할 수 있도록 id 노출.
  const getMissingRequiredOptions = (): Array<{ id: string; name: string }> => {
    const missing: Array<{ id: string; name: string }> = [];
    product.options?.forEach((option) => {
      if (!option.is_required) return;
      if (option.option_type !== "select") return;
      const cartItem = cartItems.find(
        (item) => item.type === "option" && item.optionId === option.id,
      );
      if (!cartItem?.optionValue) missing.push({ id: option.id, name: option.name });
    });
    return missing;
  };

  const validateRequiredOptions = (): boolean => {
    const missingOptions = getMissingRequiredOptions();
    if (missingOptions.length > 0) {
      setOptionErrorsVisible(true);
      toast({
        title: t("requiredOptionsTitle") || "필수 옵션을 선택해주세요",
        description:
          t("requiredOptionsMessage", { options: missingOptions.map((o) => o.name).join(", ") }) ||
          `다음 옵션을 입력해주세요: ${missingOptions.map((o) => o.name).join(", ")}`,
        variant: "destructive",
      });
      return false;
    }
    setOptionErrorsVisible(false);
    return true;
  };

  const missingRequiredOptions = getMissingRequiredOptions();
  // 사용자에게 노출되는 값은 "예약하기" 클릭으로 검증이 한 번 트리거된 후에만 채움.
  const hasMissingRequiredOptions =
    optionErrorsVisible && missingRequiredOptions.length > 0;
  const missingRequiredOptionIds = optionErrorsVisible
    ? new Set(missingRequiredOptions.map((o) => o.id))
    : new Set<string>();

  // Handle checkout
  const handleCheckout = async () => {
    if (!validateRequiredOptions()) return;

    const rentalItemsForCheckout = cartItems.filter((item) => item.type === "rental");
    const effectivePricingMode = rentalItemsForCheckout.length === 0 ? "option_only" as const : pricingMode;

    const checkoutData = buildCheckoutData({
      product,
      cartItems,
      pricingMode: effectivePricingMode,
      locale,
      dateFnsLocale,
      sourceCurrency,
      t,
    });

    if (!checkoutData) return;

    if (onCheckout) {
      onCheckout(checkoutData);
    } else {
      const result = await saveAndNavigateToCheckout(checkoutData, router, locale);
      if (!result.success) {
        toast({
          title: t("errors.storageFailed"),
          description: t("errors.storageFailedDescription"),
          variant: "destructive",
        });
      }
    }
  };

  // Handle add to cart
  const handleAddToCart = async () => {
    if (!isAuthenticated) {
      if (onAuthRequired) {
        onAuthRequired();
      } else {
        toast({
          title: t("loginRequired") || "로그인이 필요합니다",
          description: t("loginToAddToCart") || "장바구니에 추가하려면 먼저 로그인해주세요.",
          variant: "destructive",
        });
      }
      return;
    }

    if (!validateRequiredOptions()) return;

    const rentalItems = cartItems.filter((item) => item.type === "rental");
    const optionItems = cartItems.filter((item) => item.type === "option");
    if (rentalItems.length === 0 && optionItems.length === 0) {
      toast({
        title: t("noItemSelected") || "항목을 선택해주세요",
        description: t("noItemSelectedDesc") || "장바구니에 담기 전에 렌탈 기간 또는 옵션을 선택해주세요.",
        variant: "destructive",
      });
      return;
    }

    setIsAddingToCart(true);

    try {
      // P1-4: 성공한 라인은 로컬 선택에서 제거해 재시도 시 같은 실패가 무한
      // 반복되지 않게 한다(부분 성공 후 전체 재시도 → 중복/영구 실패 루프).
      const addedItemIds: string[] = [];
      for (const rentalItem of rentalItems) {
        const optionItems = cartItems.filter((item) => item.type === "option");
        const cartOptions: CartItemOptionSelection[] = optionItems.map((opt) => ({
          id: opt.optionId!,
          // 하루당 옵션: 이 대여 라인의 일수(서버 getCart 가 같은 규칙으로 다시 강제)
          quantity: opt.perDay
            ? rentalDaysForPerDay(rentalItem, pricingMode)
            : opt.quantity,
          choice_id: opt.choiceId,
          inputValue: opt.inputValue,
          hasPendingFiles: opt.pendingFiles && opt.pendingFiles.length > 0,
        }));

        const isPickupDeliveryItem = rentalItem.pickupBranchId === "delivery";
        const isReturnDeliveryItem = rentalItem.returnBranchId === "delivery";
        const pickupBranch = product.branches?.find((b) => b.id === rentalItem.pickupBranchId);
        const returnBranch = product.branches?.find((b) => b.id === rentalItem.returnBranchId);

        const pickupMethodType: "pickup" | "delivery" | "shipping" | undefined =
          isPickupDeliveryItem
            ? "delivery"
            : rentalItem.pickupBranchId
              ? pickupBranch?.branch_type === "physical"
                ? "pickup"
                : "shipping"
              : undefined;
        const returnMethodType: "pickup" | "delivery" | "shipping" | undefined =
          isReturnDeliveryItem
            ? "delivery"
            : rentalItem.returnBranchId
              ? returnBranch?.branch_type === "physical"
                ? "pickup"
                : "shipping"
              : undefined;

        // "delivery"/"shipping" are method sentinels from BranchSelector, not
        // real branch ids — never send them as pickup/return_branch_id or the
        // server's branch_info lookup fails ("Selected pickup branch not found").
        const isBranchSentinel = (v: string | undefined) =>
          v === "delivery" || v === "shipping";
        const validPickupBranchId =
          rentalItem.pickupBranchId && !isBranchSentinel(rentalItem.pickupBranchId)
            ? rentalItem.pickupBranchId
            : undefined;
        const validReturnBranchId =
          rentalItem.returnBranchId && !isBranchSentinel(rentalItem.returnBranchId)
            ? rentalItem.returnBranchId
            : undefined;

        const cartData: AddToCartInput = {
          product_id: product.id,
          partner_id: product.partner_id,
          rental_type: pricingMode,
          pickup_branch_id: validPickupBranchId,
          pickup_method_type: pickupMethodType,
          return_branch_id: validReturnBranchId,
          return_method_type: pricingMode === "daily" ? returnMethodType : undefined,
          pickup_date: formatDateToLocalISO(rentalItem.pickupDate) || "",
          pickup_time: rentalItem.pickupTime,
          return_date: pricingMode === "daily" ? formatDateToLocalISO(rentalItem.returnDate) : undefined,
          return_time: pricingMode === "daily" ? rentalItem.returnTime : undefined,
          timeslot_id: rentalItem.timeSlotId,
          timeslot_duration_hours: rentalItem.timeSlotDuration,
          // Timeslot v2 좌표 (Phase B-4) — 서버가 셀에서 단가 재확정
          sub_display_order: rentalItem.subDisplayOrder,
          option_display_order: rentalItem.optionDisplayOrder,
          quantity: rentalItem.quantity,
          options: cartOptions,
        };

        const result = await addToCartWithGuard(cartData);

        if (!result.success) {
          // User dismissed the partner-conflict modal — no error toast.
          if (result.cancelled) {
            setIsAddingToCart(false);
            return;
          }
          toast({
            title: t("addToCartFailed") || "장바구니 추가 실패",
            description:
              // 서버 코드 → 로케일 문구 (P1-4, 원문 코드 노출 방지)
              result.error === "MAX_QUANTITY_EXCEEDED"
                ? t("maxPurchaseLimitExceeded")
                : result.error || t("addToCartFailedDesc") || "장바구니에 추가하는 중 오류가 발생했습니다.",
            variant: "destructive",
          });
          // 이미 담긴 라인은 선택에서 제거 — 재시도 무한 실패 루프 방지 (P1-4)
          if (addedItemIds.length > 0) {
            setCartItems((prev) =>
              prev.filter((item) => !addedItemIds.includes(item.id)),
            );
          }
          setIsAddingToCart(false);
          return;
        }
        addedItemIds.push(rentalItem.id);
      }

      const newCount = await getCartItemCount();
      dispatchCartUpdate(newCount);
      setCartItems([]);

      if (variant === "mobile") {
        setIsDrawerOpen(false);
      }

      setShowCartConfirmation(true);
    } catch (error) {
      console.error("Add to cart error:", error);
      toast({
        title: t("addToCartFailed") || "장바구니 추가 실패",
        description: t("addToCartFailedDesc") || "장바구니에 추��하는 중 오류가 발생���습니다.",
        variant: "destructive",
      });
    } finally {
      setIsAddingToCart(false);
    }
  };

  return {
    // Form state
    selectedPickupBranch,
    selectedReturnBranch,
    pickupDate,
    returnDate,
    pickupTime,
    returnTime,
    selectedTimeSlot,
    // Timeslot v2 (Phase B-3)
    selectedTimeOptionOrder,
    setSelectedTimeOptionOrder,
    selectedSubOrder,
    setSelectedSubOrder,
    selectedOptions,
    isDrawerOpen,
    isDateDrawerOpen,
    isPickupOpen,
    // Setters
    setPickupDate,
    setReturnDate,
    setPickupTime,
    setReturnTime,
    setSelectedTimeSlot,
    setIsDrawerOpen,
    setIsDateDrawerOpen,
    setIsPickupOpen,
    setSelectedOptions,
    // Handlers
    handlePickupBranchChange,
    handleReturnBranchChange,
    handleCheckout,
    handleAddToCart,
    addOptionToCart,
    updateCartItemQuantity,
    removeCartItem,
    editRentalItem,
    checkAndAddRentalToCart,
    // Derived
    totalPrice,
    // 하루당 옵션(09-30) — 옵션 상자·선택 내역 표시용
    perDayOrders,
    perDayDays,
    isAddingToCart,
    hasMissingRequiredOptions,
    missingRequiredOptionIds,
    showCartConfirmation,
    setShowCartConfirmation,
    // Partner-conflict modal — consumer must render this somewhere.
    addToCartGuardDialog,
  };
}
